import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSafeguardSettings,
  listHeldSignups,
  reviewSignup,
  type SafeguardSettings,
  updateSafeguardSettings,
} from "@/core/lib/admin-api";

const heldKey = ["admin", "safeguards", "held"] as const;
const settingsKey = ["admin", "safeguards", "settings"] as const;

/** Sign-ups held for review (admin only). */
/** Shared so the review route's loader and this hook use one cache entry. */
export const heldSignupsQuery = {
  queryKey: heldKey,
  queryFn: listHeldSignups,
  staleTime: 30 * 1000,
};

export function useHeldSignups() {
  return useQuery(heldSignupsQuery);
}

/** Approves or rejects one held sign-up, then refreshes the queue. */
export function useReviewSignup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reviewSignup,
    onSettled: () => queryClient.invalidateQueries({ queryKey: heldKey }),
  });
}

/** The sign-up hold threshold and blocked email domains (admin only). */
export function useSafeguardSettings() {
  return useQuery({ queryKey: settingsKey, queryFn: getSafeguardSettings, staleTime: 60 * 1000 });
}

export function useUpdateSafeguardSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SafeguardSettings) => updateSafeguardSettings(input),
    onSuccess: (saved) => queryClient.setQueryData(settingsKey, saved),
  });
}
